import type { PolicyBody, PolicySource } from '@kept/shared';
import { policyBody } from '@kept/shared';
import { useState, type FormEvent } from 'react';
import { api, type Plan, type PolicyVersion } from '../lib/api.ts';
import { shortDate } from '../lib/describe.ts';
import { today } from '../lib/plantime.ts';
import { ErrorNote, Field, Icon, Sheet, useAction, useLoad } from '../ui.tsx';

export function describePolicy(p: PolicyBody): string {
  switch (p.kind) {
    case 'rightOfFirstRefusal':
      return p.threshold === 'overnight'
        ? 'Right of first refusal: when a parent needs child care overnight or longer, the other parent is asked first.'
        : `Right of first refusal: when a parent needs child care for ${p.threshold.hours} hours or more, the other parent is asked first.`;
    case 'gracePeriod': {
      const same = p.pickupMinutes === p.returnMinutes;
      const mins = same ? `${p.pickupMinutes} minutes for pickup and return` : `${p.pickupMinutes} minutes for pickup, ${p.returnMinutes} for return`;
      return `Grace period: ${mins}.${p.consequence ? ` Beyond it: ${p.consequence}` : ''}`;
    }
    case 'notice':
      return `Notice: a cancellation needs at least ${p.hours} hours’ notice.`;
  }
}

function source(s: PolicySource): string {
  return s.kind === 'plan' ? `From the plan, ${s.clause}` : `Agreed by the parents: ${s.note}`;
}

const policyOf = (v: PolicyVersion) => ({ ...(v.params as object), kind: v.kind }) as PolicyBody;

export function Policies({ plan }: { plan: Plan }) {
  const list = useLoad(() => api.policies(plan.id), [plan.id]);
  const [adding, setAdding] = useState(false);
  const [ending, setEnding] = useState<PolicyVersion | null>(null);
  const current = latest(list.data ?? []);
  return (
    <>
      <div className="title-row">
        <div>
          <h1>Policies</h1>
          <p className="finding">
            Terms that shape no custody time but matter when an entry is judged: grace periods, notice, first refusal. Each one records where it comes from,
            so a term the parents agreed is never presented as the court’s.
          </p>
        </div>
        <div className="actions">
          <button className="btn btn-primary" onClick={() => setAdding(true)}>
            <Icon name="plus" />
            Add a policy
          </button>
        </div>
      </div>
      <section className="panel" style={{ maxWidth: 760 }}>
        <ErrorNote error={list.error} />
        {list.data && current.length === 0 && <p className="empty">No policies yet.</p>}
        <div className="rows">
          {current.map((v) => (
            <div className="row" key={v.policyId}>
              <p className="row-text" style={v.ended ? { color: 'var(--muted)' } : undefined}>
                {describePolicy(policyOf(v))}
              </p>
              <div className="row-meta">
                <span>{source(v.source)}</span>
                <span>{v.ended ? `Ended ${shortDate(v.effectiveFrom)}` : `In effect from ${shortDate(v.effectiveFrom)}`}</span>
              </div>
              {!v.ended && (
                <div className="row-actions">
                  <button className="btn btn-ghost" onClick={() => setEnding(v)}>
                    End
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
      {adding && (
        <Sheet title="Add a policy" onClose={() => setAdding(false)}>
          <PolicyForm
            plan={plan}
            onSaved={() => {
              setAdding(false);
              list.reload();
            }}
          />
        </Sheet>
      )}
      {ending && (
        <Sheet title="End this policy" onClose={() => setEnding(null)}>
          <EndPolicy
            plan={plan}
            policy={ending}
            onDone={() => {
              setEnding(null);
              list.reload();
            }}
          />
        </Sheet>
      )}
    </>
  );
}

function latest(vs: PolicyVersion[]): PolicyVersion[] {
  const map = new Map<string, PolicyVersion>();
  for (const v of vs) if (!map.has(v.policyId) || map.get(v.policyId)!.version < v.version) map.set(v.policyId, v);
  return [...map.values()].map((v) => (v.ended ? { ...v, params: vs.find((x) => x.policyId === v.policyId && !x.ended)?.params ?? v.params } : v));
}

function PolicyForm({ plan, onSaved }: { plan: Plan; onSaved: () => void }) {
  const [kind, setKind] = useState<PolicyBody['kind']>('gracePeriod');
  const [overnight, setOvernight] = useState(true);
  const [hours, setHours] = useState('');
  const [pickup, setPickup] = useState('20');
  const [ret, setRet] = useState('20');
  const [consequence, setConsequence] = useState('');
  const [from, setFrom] = useState<'plan' | 'agreed'>('plan');
  const [clause, setClause] = useState('');
  const [note, setNote] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today(plan.timeZone));
  const { busy, error, setError, run } = useAction();

  const body =
    kind === 'rightOfFirstRefusal'
      ? { kind, threshold: overnight ? 'overnight' : { hours: Number(hours) } }
      : kind === 'gracePeriod'
        ? { kind, pickupMinutes: Number(pickup), returnMinutes: Number(ret), ...(consequence.trim() ? { consequence: consequence.trim() } : {}) }
        : { kind, hours: Number(hours) };
  const parsed = policyBody.safeParse(body);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!parsed.success) return setError(parsed.error.issues.map((i) => i.message).join(' '));
    const src: PolicySource = from === 'plan' ? { kind: 'plan', clause: clause.trim() } : { kind: 'agreed', note: note.trim() };
    void run(async () => {
      await api.addPolicy(plan.id, { effectiveFrom, source: src, policy: parsed.data });
      onSaved();
    });
  };

  return (
    <form className="form" onSubmit={submit}>
      <div className="seg" role="group" aria-label="Kind of policy">
        {(
          [
            ['gracePeriod', 'Grace period'],
            ['rightOfFirstRefusal', 'First refusal'],
            ['notice', 'Notice'],
          ] as const
        ).map(([k, t]) => (
          <button type="button" key={k} aria-pressed={kind === k} onClick={() => setKind(k)}>
            {t}
          </button>
        ))}
      </div>
      {kind === 'gracePeriod' && (
        <>
          <div className="two">
            <Field label="Minutes allowed at pickup">
              <input className="input" inputMode="numeric" required value={pickup} onChange={(e) => setPickup(e.target.value)} />
            </Field>
            <Field label="Minutes allowed at return">
              <input className="input" inputMode="numeric" required value={ret} onChange={(e) => setRet(e.target.value)} />
            </Field>
          </div>
          <Field label="What happens beyond it" hint="As the plan words it, e.g. “the weekend is forfeited”">
            <input className="input" value={consequence} onChange={(e) => setConsequence(e.target.value)} />
          </Field>
        </>
      )}
      {kind === 'rightOfFirstRefusal' && (
        <>
          <label className="check">
            <input type="checkbox" checked={overnight} onChange={(e) => setOvernight(e.target.checked)} />
            Applies to care overnight or longer
          </label>
          {!overnight && (
            <Field label="Applies from this many hours">
              <input className="input" inputMode="decimal" required value={hours} onChange={(e) => setHours(e.target.value)} />
            </Field>
          )}
        </>
      )}
      {kind === 'notice' && (
        <Field label="Hours of notice a cancellation needs">
          <input className="input" inputMode="decimal" required value={hours} onChange={(e) => setHours(e.target.value)} />
        </Field>
      )}
      <div className="preview">
        <small>Reads as</small>
        {parsed.success ? describePolicy(parsed.data) : <span className="muted">Fill in the fields above.</span>}
      </div>
      <fieldset className="field">
        <legend>Where it comes from</legend>
        <div className="seg" role="group">
          <button type="button" aria-pressed={from === 'plan'} onClick={() => setFrom('plan')}>
            The plan
          </button>
          <button type="button" aria-pressed={from === 'agreed'} onClick={() => setFrom('agreed')}>
            Agreed by the parents
          </button>
        </div>
      </fieldset>
      {from === 'plan' ? (
        <Field label="Plan clause">
          <input className="input" required value={clause} onChange={(e) => setClause(e.target.value)} />
        </Field>
      ) : (
        <Field label="What was agreed, and how" hint="Reports show this, labelled as an agreement, not an order">
          <textarea className="input" required value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      )}
      <Field label="Takes effect on">
        <input className="input" type="date" required value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
      </Field>
      <ErrorNote error={error} />
      <div className="actions">
        <button className="btn btn-primary" disabled={busy}>
          Add policy
        </button>
      </div>
    </form>
  );
}

function EndPolicy({ plan, policy, onDone }: { plan: Plan; policy: PolicyVersion; onDone: () => void }) {
  const [date, setDate] = useState(today(plan.timeZone));
  const [reason, setReason] = useState('');
  const { busy, error, run } = useAction();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.addPolicyVersion(plan.id, policy.policyId, { effectiveFrom: date, ended: true, reason: reason.trim() });
      onDone();
    });
  };
  return (
    <form className="form" onSubmit={submit}>
      <div className="preview">{describePolicy(policyOf(policy))}</div>
      <Field label="No longer applies from">
        <input className="input" type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Field label="Why it ended">
        <input className="input" required value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <ErrorNote error={error} />
      <div className="actions">
        <button className="btn btn-primary" disabled={busy}>
          End policy
        </button>
      </div>
    </form>
  );
}
